import { useAuth, useClerk, useSignIn } from '@clerk/clerk-react';
import { Button, PasswordConditions, Textfield, doPasswordsMatch, isPasswordValid } from '@flexo/atoms';
import { IconWrapper, getSavedLang, httpService, REGEX, triggerKeychainSave } from '@flexo/general';
import { useFormValidation, useKeyboardAwareScroll } from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { setParams } from 'libs/reducers/src/lib/calendar-reducer/calendar.reducer';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './forgot-password.module.scss';


export enum ForgotPasswordStage {
  EnterEmail = 1,
  EnterRecoveryCode = 2,
  ResetPassword = 3,
  RecoveryLinkSent = 4,
  EndUserResetSuccess = 5,
}

export interface InputErrors {
  email: boolean;
  code: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

const ENV = (import.meta as any).env;

export function ForgotPassword({ containedLabelTextfield }: any) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, isLoaded: isSignInLoaded, setActive } = useSignIn();
  const {
    getToken,
    isLoaded: isAuthLoaded,
    isSignedIn,
    signOut,
  } = useAuth();
  const clerk = useClerk();
  // CM-5140: on iOS Safari the on-screen keyboard covers the bottom of the layout
  // viewport without shrinking it, so the Ionic scroller never gains the room to
  // bring the confirm field above the keyboard. The keyboard height is measured
  // live by the shared watcher and handed to the module container, which
  // reserves it after its last widget (the language selector follows the card).
  const { keyboardHeight, keyboardVisible } = useKeyboardAwareScroll();
  const keyboardInset =
    ENV.VITE_APP_IS_WEB === 'true' && keyboardVisible && keyboardHeight > 0
      ? keyboardHeight
      : 0;
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = cardRef.current?.closest<HTMLElement>('.WidgetsContainer');
    if (!container) return undefined;
    if (keyboardInset > 0) {
      container.style.setProperty('--keyboard-inset', `${keyboardInset}px`);
    } else {
      container.style.removeProperty('--keyboard-inset');
    }
    return () => container.style.removeProperty('--keyboard-inset');
  }, [keyboardInset]);

  const params = queryString.parse(window.location.search) as any;
  const queryEmail = params?.email || null;
  const queryRecoveryCode = params?.reset_code || null;
  // Locale the reset was started in, carried by the email link (?lang=…). Used for
  // the password-change confirmation email so it matches the reset language instead
  // of recomputing browserLanguage on this (link-landing) page load.
  const queryLang = params?.lang || null;


  const [email, setEmail] = useState(queryEmail || '');
  const [recoveryCode, setRecoveryCode] = useState(queryRecoveryCode || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStage, setCurrentStage] = useState<ForgotPasswordStage>(
    (queryEmail &&queryRecoveryCode)
      ? ForgotPasswordStage.ResetPassword
      : queryEmail
        ? ForgotPasswordStage.EnterRecoveryCode
        : ForgotPasswordStage.EnterEmail
  );
  const [hasResent, setHasResent] = useState(false);
  const [isEndUser, setIsEndUser] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const loader = '/loaderLottie.json';
  const resetPasswordFormId = 'forgot-password-reset-form';
  const [loading, setLoading] = useState(false);

  const [inputType, setInputType] = useState({
    newPassword: 'password',
    confirmPassword: 'password',
  });
  const [icon, setIcon] = useState({
    newPassword: 'visibility',
    confirmPassword: 'visibility',
  });

  // Form validation hook
  const {
    errors: passwordErrors,
    blur: passwordFieldsBlur,
    passwordBlur,
    passwordValidation,
    handleBlur: hookHandleBlur,
    handleFocus: hookHandleFocus,
    handlePasswordValidation,
  } = useFormValidation(
    {
      fields: ['newPassword', 'confirmPassword'],
      hasPasswordFields: true,
    },
    { newPassword, confirmPassword }
  );

  const [errors, setErrors] = useState<InputErrors>({
    email: false,
    code: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [blur, setBlur] = useState({
    email: false,
    code: false,
  });

  const savedLang = getSavedLang() || null;
  const browserLanguage = savedLang?.split('_')[1] || navigator.language.split('-')[0] || ENV.VITE_APP_LANGUAGE;


  // Create refs for all input fields
  const emailRef = useRef<HTMLInputElement>(null);
  const recoveryCodeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const hasPreparedAuthFlow = useRef(false);

  useEffect(() => {
    if (!isAuthLoaded || !isSignInLoaded) return;
    if (hasPreparedAuthFlow.current) return;
    hasPreparedAuthFlow.current = true;

    let isCancelled = false;

    const prepareForgotPasswordFlow = async () => {
      if (!isSignedIn) {
        setAuthReady(true);
        return;
      }

      const resetPasswordUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      sessionStorage.setItem('passwordResetInProgress', 'true');
      try {
        // No redirectUrl: a redirect on the same URL is non-deterministic across
        // Clerk SDK versions (sometimes reloads, sometimes doesn't), and a reload
        // wipes React state mid-flow. Plain signOut() still broadcasts the
        // sign-out to other tabs via Clerk's cross-tab channel, which is what
        // we need. Then wait 300ms to let the client settle before signIn.create
        // can run from the form (same buffer used by web-log-in.tsx).
        await signOut();
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (!isCancelled) {
          window.history.replaceState(null, '', resetPasswordUrl);
          setAuthReady(true);
        }
      } catch (error) {
        console.error('Forgot password signOut failed:', error);
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
      } finally {
        sessionStorage.removeItem('passwordResetInProgress');
      }
    };

    prepareForgotPasswordFlow();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, isAuthLoaded, isSignInLoaded, isSignedIn, signOut]);

  // Reset resent state when stage changes away from recovery code
  useEffect(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) {
      setHasResent(false);
    }
  }, [currentStage]);

  // Focus appropriate input field when stage changes
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      let targetRef: React.RefObject<HTMLInputElement> | null = null;

      if (currentStage === ForgotPasswordStage.EnterEmail) {
        targetRef = emailRef;
      } else if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
        targetRef = recoveryCodeRef;
      } else if (currentStage === ForgotPasswordStage.ResetPassword) {
        targetRef = passwordRef;
      }

      if (targetRef?.current) {
        targetRef.current.focus();
      }
    }, 250);

    return () => clearTimeout(focusTimer);
  }, [currentStage]);

  const isEmail = useCallback((email: string) => {
    return REGEX.email.test(email);
  }, []);

  const isValidEmail = useCallback(
    (email: string) => {
      return isEmail(email);
    },
    [isEmail]
  );

  const isValidRecoveryCode = useCallback((code: string) => {
    return code.length >= 4;
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      switch (name) {
        case 'email':
          setEmail(value);
          break;
        case 'recoveryCode':
          setRecoveryCode(value);
          break;
        case 'newPassword':
          setNewPassword(value);
          handlePasswordValidation(value, confirmPassword);
          break;
        case 'confirmPassword':
          setConfirmPassword(value);
          handlePasswordValidation(newPassword, value);
          break;
      }
    },
    [newPassword, confirmPassword, handlePasswordValidation]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      // Use hook's handler for password fields
      if (name === 'newPassword' || name === 'confirmPassword') {
        hookHandleBlur(event);
        return;
      }

      if (name === 'email' && value.length) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: !isEmail(value),
        }));
      }
    },
    [hookHandleBlur, isEmail]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;

      // Use hook's handler for password fields
      if (name === 'newPassword' || name === 'confirmPassword') {
        hookHandleFocus(event);
        return;
      }

      if (name === 'email') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: false,
        }));
      }
    },
    [hookHandleFocus]
  );
  const requestRecoveryCode = useCallback(async () => {
    if (!isEmail(email)) return false;
    setLoading(true);

    try {
      // Use new Clerk-based auth endpoint
      const response = await httpService.post({
        url: `${ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL}/common/auth/v1/password-forgot`,
        data: { email: email, locale: browserLanguage, organization_slug: ENV.VITE_APP_CLERK_SLUG },
      });

      if (response.status === 200) {
        setParams({
          email: email,
        });
        const resetMethod = response.data?.reset_method;
        if (resetMethod === 'link') {
          setCurrentStage(ForgotPasswordStage.RecoveryLinkSent);
        } else {
          setIsEndUser(true);
          setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        }

        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
      );
      return false;
    } finally {
      setLoading(false);
    }
  }, [email, ENV.VITE_APP_BASE_URL, isEmail, dispatch, browserLanguage ]);

  const verifyRecoveryCode = useCallback(async () => {
    // In the new Clerk flow, we don't verify the code here.
    // The temporary password is verified when we login with Clerk in the resetPassword step.
    // Just proceed to the next stage.
    if (!recoveryCode) {
      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_INVALID_CREDENTIALS',
            } as any,
          },
        } as any)
      );
      return;
    }

    setParams({
      reset_code: recoveryCode,
    });
    setCurrentStage(ForgotPasswordStage.ResetPassword);
  }, [recoveryCode, dispatch]);

  const resetPassword = useCallback(async (e?: any) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (newPassword !== confirmPassword) return;
    if (!signIn) {
      console.error('Clerk signIn not available');
      dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
      return;
    }
    setLoading(true);

    try {
      // Step 1: Login with Clerk using email + temporary password (recoveryCode)
      const signInResult = await signIn.create({
        identifier: email,
        password: recoveryCode, // The temporary password sent via email
      });

      if (signInResult.status !== 'complete') {
        // Login failed - invalid temporary password
        console.error('Clerk login failed:', signInResult.status);
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        setErrors((prevErrors) => ({ ...prevErrors, code: true }));
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_INVALID_CREDENTIALS' } as any }));
        return;
      }

      // Flag: prevent app.tsx session-effect from signing out during this flow
      sessionStorage.setItem('passwordResetInProgress', 'true');

      // Step 2: Set the active session
      await setActive({ session: signInResult.createdSessionId });

      // Step 2b: Detect end-user role from Clerk membership data.
      // The state flag `isEndUser` is only set when the user goes through the
      // code-entry flow (requestRecoveryCode). When they click the email link
      // directly, they land on the ResetPassword stage with isEndUser=false, so
      // we'd misroute them to /login at success. Read the role from the active
      // user's organization memberships instead. We only READ here — no
      // setActive(org), which would trigger the pending-session revocation for
      // org:end_user under force_organization_selection=true.
      // Total budget: 20 * 300ms = 6s. The loop breaks as soon as memberships
      // load (usually <300ms for warm clients, longer on first sign-in).
      let resolvedIsEndUser = isEndUser;
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const memberships = clerk.user?.organizationMemberships || [];
        if (memberships.length > 0) {
          const role = memberships[0].role;
          resolvedIsEndUser = role !== 'org:admin' && role !== 'org:supervisor';
          break;
        }
      }

      // Step 3: Get JWT and call password-change BEFORE setActive(organization).
      // The user's password is per-user, not per-org, so org context isn't needed
      // to change it. Calling setActive(org) here would put the session into pending
      // state for users with role org:end_user under force_organization_selection=true,
      // and Clerk auto-revokes pending sessions within ~300ms — too fast for the
      // password-change roundtrip. The redirect below handles routing without needing
      // an active org: end-user web → /access-denied (signed out), admin/supervisor →
      // /login (will re-sign-in fresh).
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to get JWT token after login');
      }

      const response = await httpService.post({
        url: `${ENV.VITE_APP_CUSTOM_BASE_URL || ENV.VITE_APP_BASE_URL}/common/auth/v1/password-change`,
        data: {
          new_password: newPassword,
          // Prefer the reset link's lang so the confirmation email matches the
          // language the reset was started in (falls back to the UI language).
          locale: queryLang || browserLanguage,
        },
        idToken: token,
        skipLogoutOn401: true,
      });

      if (response.status === 200) {
        triggerKeychainSave(email, newPassword);
        if (resolvedIsEndUser && ENV.VITE_APP_IS_WEB === 'true') {
          // End-user web (FLEXO-6062): web now supports end users, so after a
          // successful reset send them to /login to re-enter the end-user web
          // experience (previously redirected to /access-denied with a
          // "use the mobile app" message).
          localStorage.setItem('passwordUpdated', 'true');
          sessionStorage.setItem('passwordUpdated', 'true');
          navigate('/login');
        } else {
          // Admin/supervisor: set flag for login page alert, then navigate
          localStorage.setItem('passwordUpdated', 'true');
          sessionStorage.setItem('passwordUpdated', 'true');
          navigate('/login');
        }
      }
    } catch (error: any) {
      sessionStorage.removeItem('enduserPasswordResetSuccess');
      console.error('Password reset failed:', error);

      // Check if error is from Clerk login (invalid temp password)
      if (error?.errors?.[0]?.code === 'form_password_incorrect' ||
          error?.errors?.[0]?.code === 'form_identifier_not_found') {
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        setErrors((prevErrors) => ({ ...prevErrors, code: true }));
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_INVALID_CREDENTIALS' } as any }));
      } else {
        dispatch(ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any }));
      }
    } finally {
      sessionStorage.removeItem('passwordResetInProgress');
      setLoading(false);
    }
  }, [
    email,
    recoveryCode,
    newPassword,
    confirmPassword,
    browserLanguage,
    queryLang,
    signIn,
    signOut,
    setActive,
    clerk,
    getToken,
    navigate,
    dispatch,
    isEndUser,
  ]);

  const togglePasswordVisibility = useCallback((name: string) => {
    setInputType((p) => ({
      ...p,
      [name]: p[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((p) => ({
      ...p,
      [name]: p[name] === 'hide-visibility' ? 'visibility' : 'hide-visibility',
    }));
  }, []);

  const handleResendCode = useCallback(async () => {
    const recoveryCodeRequested = await requestRecoveryCode();
    if (recoveryCodeRequested) {
      setHasResent(true);
    }
  }, [requestRecoveryCode]);





  const renderCustomSubtitle = useCallback(() => {


    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) return null;

    return (
      <div className={styles.ForgotPassword__RecoveryText}>
        <p>
          {t('pages.forgotten_password.recoveryCode_subtitle.line_1')}{' '}
          <strong>{email}</strong>{' '}
          {t('pages.forgotten_password.recoveryCode_subtitle.line_2')}
        </p>
        <p>
          {t('pages.forgotten_password.recoveryCode_subtitle.line_3')}{' '}
          {hasResent ? (
            <span className={styles.ForgotPassword__ResentText}>
              {t('pages.forgotten_password.recoveryCode_subtitle.resentLabel')}
              <IconWrapper iconName="check-encircled_outline" />
            </span>
          ) : (
            <button
              onClick={handleResendCode}
              className={styles.ForgotPassword__ResendButton}
            >
              {t('pages.forgotten_password.recoveryCode_subtitle.buttonLabel')}
            </button>
          )}
        </p>
      </div>
    );
  }, [currentStage, t, email, hasResent, handleResendCode]);

  const stageConfig = useMemo(
    () => ({
      [ForgotPasswordStage.EnterEmail]: {
        title: t('pages.forgot_password.data.title'),
        subtitle: t('pages.forgot_password.data.subtitle'),
        buttonLabel: t('pages.forgot_password.data.buttonLabel'),
        action: (e: any) => {
          e.stopPropagation();
          e.preventDefault()
          requestRecoveryCode();

        },
        isDisabled: () => !email || !isValidEmail(email) || loading,
        inputComponent: (
          <form className={styles.ForgotPassword__Form}>
            <Textfield
              textfieldColor='grey'
              name="email"
              ref={emailRef}
              value={email}
              placeholder={
                t('pages.forgot_password.data.inputs.0.placeholder') as string
              }
              label={t('pages.forgot_password.data.inputs.0.label') as string}
              onChange={handleInputChange}
              type="email"
              containedLabel={false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
            />
          </form>
        ),
      },
      [ForgotPasswordStage.EnterRecoveryCode]: {
        title: t('pages.recovery_code_data.data.title'),
        subtitle: null,
        buttonLabel: t('pages.recovery_code_data.data.buttonLabel'),
        action: verifyRecoveryCode,
        isDisabled: () =>
          !recoveryCode || !isValidRecoveryCode(recoveryCode) || loading,
        inputComponent: (
          <form className={styles.ForgotPassword__Form}>
            <Textfield
              textfieldColor='grey'
              name="recoveryCode"
              ref={recoveryCodeRef}
              value={recoveryCode}
              placeholder={
                t(
                  'pages.recovery_code_data.data.inputs.0.placeholder'
                ) as string
              }
              label={
                t('pages.recovery_code_data.data.inputs.0.label') as string
              }
              onChange={handleInputChange}
              type="text"
              containedLabel={false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
            />
          </form>
        ),
      },
      [ForgotPasswordStage.ResetPassword]: {
        title: t('pages.password_reset_web.title'),
        subtitle: t('pages.password_reset_web.subtitle', { email }),
        buttonLabel: t('pages.password_reset_web.buttonLabel'),
        action: resetPassword,
        isDisabled: () => {
          if (!newPassword || !confirmPassword) return true;
          return (
            !isPasswordValid(newPassword) ||
            !doPasswordsMatch(newPassword, confirmPassword) ||
            loading
          );
        },
        inputComponent: (
          <form
            id={resetPasswordFormId}
            className={styles.ForgotPassword__Form}
            onSubmit={resetPassword}
          >
            {/* Hidden email field for keychain/password manager capture */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              readOnly
              style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              tabIndex={-1}
            />
            <Textfield
              textfieldColor='grey'
              name="newPassword"
              ref={passwordRef}
              value={newPassword}
              placeholder={
                t(
                  'pages.password_reset_web.inputs.password.placeholder'
                ) as string
              }
              label={
                t('pages.password_reset_web.inputs.password.label') as string
              }
              onChange={handleInputChange}
              type={inputType.newPassword}
              icon={icon.newPassword}
              onIconClick={() => togglePasswordVisibility('newPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
              containedLabel={false}
              autoComplete="new-password"
            />
            <PasswordConditions
              className={styles.PasswordConditions}
              validation={passwordValidation}
              isFocused={!passwordBlur}
              conditionClassName={styles.ForgotPassword__Conditions}
            />
            <Textfield
              textfieldColor='grey'
              variant="default"
              name="confirmPassword"
              ref={confirmPasswordRef}
              value={confirmPassword}
              placeholder={
                t(
                  'pages.password_reset_web.inputs.password_confirm.placeholder'
                ) as string
              }
              label={
                t(
                  'pages.password_reset_web.inputs.password_confirm.label'
                ) as string
              }
              onChange={handleInputChange}
              type={inputType.confirmPassword}
              icon={icon.confirmPassword}
              onIconClick={() => togglePasswordVisibility('confirmPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
              containedLabel={false}
              autoComplete="off"
              error={passwordErrors.confirmPassword && passwordFieldsBlur.confirmPassword}
              errorDescription={
                passwordErrors.confirmPassword && passwordFieldsBlur.confirmPassword
                  ? t('pages.password_reset_web.inputs.password_confirm.error') as string
                  : ''
              }
            />
          </form>
        ),
      },
      [ForgotPasswordStage.RecoveryLinkSent]: {
        title: t('pages.recovery_link_sent.title'),
        subtitle: null,
        buttonLabel: '',
        action: () => { /* no-op: stage has no submit action */ },
        isDisabled: () => true,
        inputComponent: (
          <div className={styles.ForgotPassword__LinkSent}>
            <p>
              {t('pages.recovery_link_sent.description_1')}{' '}
              <strong>{email}</strong>{' '}
              {t('pages.recovery_link_sent.description_2')}
            </p>
            <p>
              {t('pages.recovery_link_sent.not_received')}{' '}
              {hasResent ? (
                <span className={styles.ForgotPassword__ResentText}>
                  {t('pages.recovery_link_sent.resent')}
                  <IconWrapper iconName="check-encircled_outline" />
                </span>
              ) : (
                <button
                  onClick={handleResendCode}
                  className={styles.ForgotPassword__ResendButton}
                >
                  {t('pages.recovery_link_sent.resend')}
                </button>
              )}
            </p>
          </div>
        ),
      },
      [ForgotPasswordStage.EndUserResetSuccess]: {
        title: t('pages.enduser_reset_success.title'),
        subtitle: null,
        buttonLabel: '',
        action: () => { /* no-op: stage has no submit action */ },
        isDisabled: () => true,
        inputComponent: (
          <div className={styles.ForgotPassword__LinkSent}>
            <p>
              {t('pages.enduser_reset_success.description_1')}{' '}
              <strong>{email}</strong>{' '}
              {t('pages.enduser_reset_success.description_2')}
            </p>
          </div>
        ),
      },
    }),
    [
      t,
      email,
      isValidEmail,
      isValidRecoveryCode,
      isPasswordValid,
      doPasswordsMatch,
      handleInputChange,
      handleBlur,
      handleFocus,
      recoveryCode,
      newPassword,
      confirmPassword,
      inputType,
      icon,
      togglePasswordVisibility,
      passwordValidation,
      passwordBlur,
      passwordErrors,
      passwordFieldsBlur,
      requestRecoveryCode,
      verifyRecoveryCode,
      resetPassword,
      handleResendCode,
      hasResent,
      loading,
    ]
  );

  const currentStageConfig = useMemo(
    () => stageConfig[currentStage],
    [stageConfig, currentStage]
  );

  const handleKeyPress = useCallback(

    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (!currentStageConfig.isDisabled() && !loading) {
          event.preventDefault();
          event.stopPropagation();

          (currentStageConfig as any).action( event );
        }
      }

    },
    [currentStageConfig, loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  if (!authReady) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className={`${styles.ForgotPassword} ${ENV.VITE_APP_IS_WEB === 'true' && styles.ForgotPassword__Web}`}
    >
      <div className={styles.ForgotPassword__Logo}>
        <IconWrapper iconName="logo_wide" />
      </div>
      <div className={styles.ForgotPassword__Content}>
        <div className={styles.ForgotPassword__Header}>
          <div className={styles.ForgotPassword__Header__Title}>
            {currentStageConfig.title}
          </div>
          {currentStageConfig.subtitle && (
            <div className={styles.ForgotPassword__Header__Subtitle}>
              {currentStageConfig.subtitle}
            </div>
          )}
          {renderCustomSubtitle()}
        </div>

        {currentStageConfig.inputComponent}

        {currentStage === ForgotPasswordStage.RecoveryLinkSent || currentStage === ForgotPasswordStage.EndUserResetSuccess ? (
          null
        ) : currentStage === ForgotPasswordStage.EnterEmail ? (
          <div className={styles.ForgotPassword__FooterWithLogin}>
            <div className={styles.ForgotPassword__BackToLogin}>
              <span>{t('pages.forgot_password.data.return_label.label')}</span>
              <button
                onClick={() => navigate('/login')}
                className={styles.ForgotPassword__BackToLogin__Button}
              >
                {t('pages.forgot_password.data.return_label.buttonLabel')}
              </button>
            </div>
            <Button
              type="submit"
              className={styles.ForgotPassword__Footer__Button}
              onClick={( event: any ) => currentStageConfig.action( event )}
              variant="primary"
              color="secondary"
              label={
                loading ? (
                  <Player
                    autoplay
                    loop
                    src={loader}
                    style={{ height: 20, width: 60 }}
                  />
                ) : (
                  currentStageConfig.buttonLabel
                )
              }
              disabled={currentStageConfig.isDisabled()}
            />
          </div>
        ) : (
          <div className={styles.ForgotPassword__Footer}>
              <Button
              type={currentStage === ForgotPasswordStage.ResetPassword ? 'submit' : 'button'}
              form={
                currentStage === ForgotPasswordStage.ResetPassword
                  ? resetPasswordFormId
                  : undefined
              }
              className={styles.ForgotPassword__Footer__Button}
              onClick={
                currentStage === ForgotPasswordStage.ResetPassword
                  ? undefined
                  : (event: any) => currentStageConfig.action(event)
              }
              variant="primary"
              color="secondary"
              label={
                loading ? (
                  <Player
                    autoplay
                    loop
                    src={loader}
                    style={{ height: 20, width: 60 }}
                  />
                ) : (
                  currentStageConfig.buttonLabel
                )
              }
              disabled={currentStageConfig.isDisabled()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
